import type { JSX } from 'react';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent } from '@/components/ui/card';
import { formatDateInfo } from '@/features/experiences/types/experience.types';
import type { CertificateExperience } from '../types/certificate.types';
import { CertificateRefereeStatement } from './CertificateRefereeStatement';

export interface CertificateExperienceCardProps {
  experience: CertificateExperience;
}

function periodLabel(experience: CertificateExperience): string | null {
  const start = formatDateInfo(experience.startDate);
  const end = experience.isOngoing ? 'Present' : formatDateInfo(experience.endDate);
  if (!start && !end) return null;
  return `${start || 'Unknown'} — ${end || 'Unknown'}`;
}

function monthsLabel(months: number): string {
  return `${months} ${months === 1 ? 'month' : 'months'}`;
}

function attributeLabels(experience: CertificateExperience): string[] {
  return [
    experience.isFullTime ? 'Full-time' : 'Part-time',
    experience.isContractor ? 'Contractor' : null,
    experience.isOngoing ? 'Ongoing' : null,
    experience.continuous ? 'Continuous' : 'With breaks',
  ].filter((label): label is string => label !== null);
}

function ExperienceHeading({ experience }: CertificateExperienceCardProps): JSX.Element {
  const period = periodLabel(experience);

  return (
    <div className="flex flex-col gap-1">
      <h2 className="font-serif text-xl font-semibold tracking-tight">{experience.jobTitle}</h2>
      <p className="text-sm font-medium text-muted-foreground">{experience.companyName}</p>
      <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-muted-foreground">
        {period && <span data-testid="experience-period">{period}</span>}
        <span>{monthsLabel(experience.effectiveWorkMonths)}</span>
      </div>
    </div>
  );
}

/**
 * One confirmed experience rendered as a self-contained block of the document.
 */
export function CertificateExperienceCard({
  experience,
}: CertificateExperienceCardProps): JSX.Element {
  return (
    <Card data-testid="certificate-experience" className="border-border/70 shadow-none">
      <CardContent className="flex flex-col gap-4 p-5 sm:p-6">
        <ExperienceHeading experience={experience} />

        <div className="flex flex-wrap gap-2">
          {attributeLabels(experience).map((label) => (
            <Badge key={label} variant="secondary" className="font-normal">
              {label}
            </Badge>
          ))}
        </div>

        <div className="flex flex-col gap-2">
          <p className="text-[0.7rem] font-semibold uppercase tracking-[0.2em] text-muted-foreground">
            Confirmed by
          </p>
          <ul className="flex flex-col gap-3">
            {experience.verifications.map((verification, index) => (
              <CertificateRefereeStatement
                key={`${verification.refereeName}-${index}`}
                verification={verification}
              />
            ))}
          </ul>
        </div>
      </CardContent>
    </Card>
  );
}
